/**
 * CalendarPermissionsMatrix
 *
 * Modal que renderiza a matriz [perfil x permissão] de um calendário e permite
 * ajustar permissões herdadas por papel. Para supervisor/agente/usuário é
 * possível restringir a perfis de permissão específicos do tenant.
 *
 * RELEASE:
 *   2026-06-25 v1.2.0 - Layout padronizado (header premium + footer).
 *   2026-06-25 v1.1.0 - Restrição por perfil de permissão (multi-select).
 *   2026-06-24 v1.0.0 - Versão inicial (Fase 5 Calendário Global)
 */


import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Shield, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import {
  CALENDAR_MATRIX_ROLES,
  CALENDAR_PERMISSION_LABELS,
  useCalendarPermissions,
  type CalendarPermission,
  type CalendarRole,
} from '@/hooks/useCalendarPermissions';
import { useTenantPermissionProfiles } from '@/hooks/useTenantPermissionProfiles';
import type { UserCalendar } from '@/hooks/useCalendars';

/** Rótulos legíveis dos perfis em PT-BR */
const ROLE_LABELS: Record<CalendarRole, string> = {
  tenant_admin: 'Administrador',
  tenant_supervisor: 'Supervisor',
  agent: 'Agente',
  user: 'Usuário',
};

const PERMISSION_OPTIONS: CalendarPermission[] = [
  'none',
  'free_busy',
  'view',
  'edit',
  'manage',
];

/** Papéis que aceitam restrição por perfil de permissão (tenant_admin = acesso total). */
const ROLES_SUPPORT_PROFILE_RESTRICTION: CalendarRole[] = [
  'tenant_supervisor',
  'agent',
  'user',
];

interface RoleDraft {
  permission: CalendarPermission;
  /** Quando vazio = "todos os perfis do papel" (regra global). */
  profileIds: string[];
  /** Toggle visual do restritor. */
  restrict: boolean;
}

interface CalendarPermissionsMatrixProps {
  calendar: UserCalendar | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Cria estado inicial vazio (todas as roles em 'none'). */
function buildEmptyDraft(): Record<CalendarRole, RoleDraft> {
  return CALENDAR_MATRIX_ROLES.reduce(
    (acc, r) => ({
      ...acc,
      [r]: { permission: 'none', profileIds: [], restrict: false } as RoleDraft,
    }),
    {} as Record<CalendarRole, RoleDraft>,
  );
}

export function CalendarPermissionsMatrix({
  calendar,
  open,
  onOpenChange,
}: CalendarPermissionsMatrixProps) {
  const { permissions, isLoading, save, isSaving } = useCalendarPermissions(
    open ? calendar?.id : null,
  );
  const { profiles, isLoading: isLoadingProfiles } = useTenantPermissionProfiles(open);

  const [draft, setDraft] = useState<Record<CalendarRole, RoleDraft>>(buildEmptyDraft);

  /** Hidrata o draft a partir das permissões vindas do servidor. */
  useEffect(() => {
    if (!open) return;
    const base = buildEmptyDraft();
    // Agrupa por role: pega a permissão (todas as linhas da mesma role têm a
    // mesma permission pelo contrato da RPC update) e coleta profile_ids.
    for (const row of permissions) {
      if (!CALENDAR_MATRIX_ROLES.includes(row.role)) continue;
      const entry = base[row.role];
      entry.permission = row.permission;
      if (row.profile_id) {
        entry.profileIds.push(row.profile_id);
        entry.restrict = true;
      }
    }
    setDraft(base);
  }, [permissions, open]);

  const canManage = calendar?.user_permission === 'manage';

  /** Validação: restrição ligada exige ao menos 1 perfil selecionado. */
  const validationError = useMemo(() => {
    for (const role of CALENDAR_MATRIX_ROLES) {
      const r = draft[role];
      if (
        ROLES_SUPPORT_PROFILE_RESTRICTION.includes(role) &&
        r.permission !== 'none' &&
        r.restrict &&
        r.profileIds.length === 0
      ) {
        return `Selecione ao menos um perfil para ${ROLE_LABELS[role]} ou desmarque a restrição.`;
      }
    }
    return null;
  }, [draft]);

  const isDirty = useMemo(() => {
    // Reconstruir estado "atual" do servidor para comparar
    const current = buildEmptyDraft();
    for (const row of permissions) {
      if (!CALENDAR_MATRIX_ROLES.includes(row.role)) continue;
      const e = current[row.role];
      e.permission = row.permission;
      if (row.profile_id) {
        e.profileIds.push(row.profile_id);
        e.restrict = true;
      }
    }
    return CALENDAR_MATRIX_ROLES.some((r) => {
      const a = current[r];
      const b = draft[r];
      if (a.permission !== b.permission) return true;
      if (a.restrict !== b.restrict) return true;
      const setA = new Set(a.profileIds);
      const setB = new Set(b.profileIds);
      if (setA.size !== setB.size) return true;
      for (const id of setA) if (!setB.has(id)) return true;
      return false;
    });
  }, [permissions, draft]);

  const handleSave = async () => {
    const payload = CALENDAR_MATRIX_ROLES.map((role) => {
      const r = draft[role];
      const supportsProfile = ROLES_SUPPORT_PROFILE_RESTRICTION.includes(role);
      return {
        role,
        permission: r.permission,
        profile_ids:
          supportsProfile && r.restrict && r.permission !== 'none' ? r.profileIds : [],
      };
    });
    await save(payload);
    onOpenChange(false);
  };

  const updateRole = (role: CalendarRole, patch: Partial<RoleDraft>) => {
    setDraft((prev) => ({ ...prev, [role]: { ...prev[role], ...patch } }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl w-[95vw] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col sm:rounded-lg">
        {/* Header premium padronizado (mesmo padrão do modal de anotações) */}
        <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent shrink-0">
          <div className="flex items-start gap-3 min-w-0 pr-8">
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg shrink-0">
              <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
            <div className="flex flex-col min-w-0">
              <DialogTitle className="truncate">Compartilhamento do calendário</DialogTitle>
              <DialogDescription className="text-xs line-clamp-2 sm:line-clamp-none">
                Defina o nível de acesso de cada perfil do tenant para{' '}
                <span className="font-medium">{calendar?.name}</span>.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Corpo rolável */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              {CALENDAR_MATRIX_ROLES.map((role) => {
                const r = draft[role];
                const supportsProfile = ROLES_SUPPORT_PROFILE_RESTRICTION.includes(role);
                const isDisabled = !canManage || isSaving;
                const restrictDisabled = isDisabled || r.permission === 'none';
                return (
                  <div
                    key={role}
                    className="rounded-md border border-border bg-card/40 p-3 space-y-3"
                  >
                    <div className="grid grid-cols-[1fr_180px] items-center gap-3">
                      <Label className="text-sm font-medium">{ROLE_LABELS[role]}</Label>
                      <Select
                        value={r.permission}
                        onValueChange={(v) =>
                          updateRole(role, { permission: v as CalendarPermission })
                        }
                        disabled={isDisabled}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PERMISSION_OPTIONS.map((p) => (
                            <SelectItem key={p} value={p}>
                              {CALENDAR_PERMISSION_LABELS[p]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {supportsProfile && (
                      <div className="space-y-2 pl-1">
                        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                          <Checkbox
                            checked={r.restrict}
                            disabled={restrictDisabled}
                            onCheckedChange={(v) =>
                              updateRole(role, {
                                restrict: !!v,
                                profileIds: v ? r.profileIds : [],
                              })
                            }
                          />
                          Restringir a perfis de permissão específicos
                        </label>

                        {r.restrict && r.permission !== 'none' && (
                          <ProfileMultiSelect
                            profiles={profiles}
                            loading={isLoadingProfiles}
                            value={r.profileIds}
                            disabled={isDisabled}
                            onChange={(ids) => updateRole(role, { profileIds: ids })}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              <Separator className="my-2" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                <strong>Apenas livre/ocupado:</strong> outros usuários só veem horários
                ocupados, sem detalhes. <strong>Visualizar:</strong> leem todos os campos.{' '}
                <strong>Editar:</strong> criam e alteram eventos.{' '}
                <strong>Gerenciar:</strong> também ajustam permissões e excluem o
                calendário. Quando "Restringir a perfis" está marcado, apenas usuários
                atribuídos àqueles perfis de permissão recebem o acesso.
              </p>

              {!canManage && (
                <p className="text-xs text-amber-600">
                  Você não tem permissão de gerenciamento neste calendário (visualização
                  somente).
                </p>
              )}

              {validationError && (
                <p className="text-xs text-destructive">{validationError}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer padronizado */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Fechar
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              !canManage || !isDirty || isSaving || isLoading || !!validationError
            }
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar permissões
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Sub-componente: multi-select de perfis (Popover + Command, padrão shadcn)
// ============================================================================
interface ProfileMultiSelectProps {
  profiles: Array<{ id: string; name: string }>;
  loading: boolean;
  value: string[];
  disabled?: boolean;
  onChange: (ids: string[]) => void;
}

function ProfileMultiSelect({
  profiles,
  loading,
  value,
  disabled,
  onChange,
}: ProfileMultiSelectProps) {
  const [openPop, setOpenPop] = useState(false);
  const selectedSet = new Set(value);

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((x) => x !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const removeOne = (id: string) => onChange(value.filter((x) => x !== id));

  const selectedProfiles = profiles.filter((p) => selectedSet.has(p.id));

  return (
    <div className="space-y-2">
      <Popover open={openPop} onOpenChange={setOpenPop}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            disabled={disabled}
            className="w-full justify-between h-9 font-normal"
          >
            <span className="truncate text-xs">
              {loading
                ? 'Carregando perfis...'
                : value.length === 0
                ? 'Selecionar perfis de permissão'
                : `${value.length} perfil(is) selecionado(s)`}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0 ml-2" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Buscar perfil..." className="h-9" />
            <CommandList>
              <CommandEmpty>
                {profiles.length === 0
                  ? 'Nenhum perfil ativo configurado.'
                  : 'Nenhum resultado.'}
              </CommandEmpty>
              <CommandGroup>
                {profiles.map((p) => {
                  const checked = selectedSet.has(p.id);
                  return (
                    <CommandItem
                      key={p.id}
                      value={p.name}
                      onSelect={() => toggle(p.id)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          checked ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      {p.name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedProfiles.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedProfiles.map((p) => (
            <Badge key={p.id} variant="secondary" className="text-xs gap-1 pr-1">
              {p.name}
              <button
                type="button"
                disabled={disabled}
                onClick={() => removeOne(p.id)}
                className="hover:bg-muted-foreground/20 rounded-sm p-0.5"
                aria-label={`Remover ${p.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
